import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { seedDemoEmployees } from '../seedDemoEmployees';
import { clearDemoEmployees } from '../clearDemoEmployees';
import {
  isDemoEmployeesToolsEnabled,
  DEMO_EMPLOYEE_COUNT_OPTIONS,
} from '../constants';
import '../../demo-appointments/components/DemoAppointmentsPanel.css';

const DemoEmployeesPanel = () => {
  const { companyData, user } = useAuth();
  const isSpecialAdmin = isDemoEmployeesToolsEnabled(user?.isSpecialAdmin);
  const { showToast } = useNotification();
  const [count, setCount] = useState(12);
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const ownerId = companyData?.ownerId || user?.uid;

  if (!isSpecialAdmin) {
    return null;
  }

  const handleSeed = async () => {
    if (!companyData?.id || !ownerId) {
      showToast('Sign in with a company account first.', 'error');
      return;
    }

    setBusy(true);
    setIsError(false);
    setStatusMessage('Creating demo employees…');

    try {
      const result = await seedDemoEmployees({
        companyId: companyData.id,
        ownerId,
        count,
        isSpecialAdmin: true,
      });

      setStatusMessage(
        `Added ${result.count} demo team members (${result.managerCount} managers, ${result.employeeCount} staff). Open Team Management or Calendar to screenshot.`
      );
      showToast('Demo employees created', 'success');
    } catch (err) {
      console.error('[DemoEmployeesPanel] seed failed:', err);
      setIsError(true);
      const permissionHint =
        err?.code === 'permission-denied'
          ? ' Update the database access rules if you have not yet.'
          : '';
      setStatusMessage((err.message || 'Failed to create demo employees.') + permissionHint);
      showToast('Could not create demo employees', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    if (!companyData?.id) {
      showToast('Sign in with a company account first.', 'error');
      return;
    }

    setBusy(true);
    setIsError(false);
    setStatusMessage('Removing demo employees…');

    try {
      const result = await clearDemoEmployees(companyData.id, { isSpecialAdmin: true });
      setStatusMessage(result.message);
      showToast(result.deleted > 0 ? 'Demo employees removed' : 'Nothing to remove', 'info');
    } catch (err) {
      console.error('[DemoEmployeesPanel] clear failed:', err);
      setIsError(true);
      setStatusMessage(err.message || 'Failed to remove demo employees.');
      showToast('Could not remove demo employees', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="demo-appt-panel" aria-labelledby="demo-emp-panel-title">
      <h3 id="demo-emp-panel-title" className="demo-appt-panel__title">
        Screenshot demo employees
      </h3>
      <p className="demo-appt-panel__desc">
        Add a full team roster without creating real login accounts. Demo staff appear in Team
        Management, booking flows, and calendar columns. Tagged records can be removed in one click
        from this browser.
      </p>

      <div className="demo-appt-panel__row">
        <div className="demo-appt-panel__field">
          <label htmlFor="demo-emp-count">Team size</label>
          <select
            id="demo-emp-count"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            disabled={busy}
          >
            {DEMO_EMPLOYEE_COUNT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} members
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="demo-appt-panel__actions">
        <button
          type="button"
          className="demo-appt-panel__btn demo-appt-panel__btn--primary"
          onClick={handleSeed}
          disabled={busy}
        >
          {busy ? 'Working…' : 'Seed demo team'}
        </button>
        <button
          type="button"
          className="demo-appt-panel__btn demo-appt-panel__btn--ghost"
          onClick={handleClear}
          disabled={busy}
        >
          Clear demo team
        </button>
      </div>

      {statusMessage ? (
        <p className={`demo-appt-panel__status${isError ? ' demo-appt-panel__status--error' : ''}`}>
          {statusMessage}
        </p>
      ) : null}

      <p className="demo-appt-panel__hint">
        Tip: seed employees first, then use Screenshot demo appointments to fill the calendar.
        Demo staff have no passwords and cannot sign in. Only special admin accounts can use this tool.
      </p>
    </section>
  );
};

export default DemoEmployeesPanel;
